import {MechanicValidator} from './MechanicValidator'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'
import {PlayerValidator} from '../core/PlayerValidator'
import {MapValidator} from '../core/map/MapValidator'
import {ActionState, MechanicSettlementPlayerState, MechanicSettlementState, NestedReadonly, PlayerColor} from '@colonist/game-states'
import {getCardsForPiece} from '@colonist/game-card-types'
import {CurrentStateValidator} from '../core/CurrentStateValidator'

export class MechanicValidatorSettlement extends MechanicValidator {
    private readonly currentStateValidator: CurrentStateValidator
    private readonly settlementState: MechanicSettlementState

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, currentStateValidator: CurrentStateValidator, settlementState: MechanicSettlementState) {
        super(playerValidators, mapValidator)

        this.currentStateValidator = currentStateValidator
        this.settlementState = settlementState
    }

    getSettlementPlayerState(player: PlayerColor): NestedReadonly<MechanicSettlementPlayerState> {
        if(player == PlayerColor.None) {
            return {
                bankSettlementAmount: 0,
            }
        }
        const settlementPlayerState = this.settlementState[player]
        if(settlementPlayerState === undefined) throw new Error(`No settlement player state for player ${player}`)

        return settlementPlayerState
    }

    hasResourcesAndPiecesForSettlement(player: PlayerColor, useResources: boolean): ActionStatus {
        const settlementPlayerState = this.getSettlementPlayerState(player)
        if(settlementPlayerState.bankSettlementAmount <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughSettlementPieces'}}
        if(!useResources) return {canTakeAction: true}

        const resourcesForSettlement = getCardsForPiece(PieceTypes.Settlement)
        const playerController = this.playerValidators.get(player)
        if(playerController === undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}, validHoverLocations: []}

        const hasResourcesForSettlement = playerController.hasResourceCardsInHand(resourcesForSettlement)
        if(!hasResourcesForSettlement) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerPlaceSettlement(player: PlayerColor, useResources: boolean): ActionStatus {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard)
        if(isSomePlayerDiscarding) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.playersDiscarding'}}

        const where = this.whereCanPlayerPlaceSettlements(player)
        const doesPlayerHaveResourcesAndPiecesForSettlement = this.hasResourcesAndPiecesForSettlement(player, useResources)

        doesPlayerHaveResourcesAndPiecesForSettlement.validHoverLocations = where
        if(!doesPlayerHaveResourcesAndPiecesForSettlement.canTakeAction) return doesPlayerHaveResourcesAndPiecesForSettlement

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.settlementsNeedToBeTwoRoadsApart'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerPlaceSettlements(player: PlayerColor): number[] {
        const placesToBuildSettlementArray: number[] = []
        for(const edge of this.mapValidator.tileState.tileEdges) {
            if(edge.type == undefined) continue
            if(edge.owner != player) continue
            for(const endPoint of edge.hexEdge.endPoints()) {
                const tileCorner = this.mapValidator.tileState.getTileCorner(endPoint)
                if(tileCorner == undefined) continue
                if(tileCorner.owner != undefined) continue
                if(!this.mapValidator.areAdjacentCornersEmpty(tileCorner)) continue
                if(!this.mapValidator.tileState.isCornerTouchingLand(tileCorner)) continue

                const index = this.mapValidator.tileState.getTileCornerIndex(tileCorner.hexCorner)
                if(index == undefined) continue

                placesToBuildSettlementArray.push(index)
            }
        }

        return ArrayUtils.removeDuplicatesFromArray(placesToBuildSettlementArray)
    }

    whereCanPlayerPlaceBeginningSettlements(): number[] {
        const placesToBuildSettlementArray: number[] = []
        for(const tileCorner of this.mapValidator.tileState.tileCorners) {
            if(tileCorner.owner != undefined) continue
            if(!this.mapValidator.areAdjacentCornersEmpty(tileCorner)) continue
            const index = this.mapValidator.tileState.getTileCornerIndex(tileCorner.hexCorner)
            if(index == undefined) continue
            if(!this.mapValidator.tileState.isCornerTouchingLand(tileCorner)) continue
            if(tileCorner.restrictedStartingPlacement) continue
            placesToBuildSettlementArray.push(index)
        }

        return ArrayUtils.removeDuplicatesFromArray(placesToBuildSettlementArray)
    }
}
